import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  IconNavigationMore,
  IconCloseOutline,
  IconCalendar,
  IconEditPencil,
  IconEnvelope,
  IconCheveronOutlineLeft,
  IconDateAdd,
} from '@medflex/components/stories/Icons';
import Button from '@medflex/components/stories/Button';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import Tooltip from '@medflex/components/stories/Tooltip';
import config from '@medflex/utils/config';
import { trackEvent } from '@medflex/utils/Tracking';
import { dayjs } from '@medflex/shared-utils';
import isPermanentSession from '@medflex/utils/isPermanentSession';
import { SESSION_TYPES, CLIENT_TYPES } from '@medflex/utils/constants';
import { SessionProviders } from '@medflex/shared-constants';
import useSessionRoles from '../../hooks/useSessionRoles';
import useModal from '../../hooks/useModal';
import getParticipantsWithoutOrganizer from '../../../utils/getParticipantsWithoutOrganizer';
import { MODALS } from '../../hooks/useIsModal';
import useSessionMeetings from '../../hooks/useSessionMeetings';

const { SESSIONS_URL, PUBLIC_INSTANT_MESSAGE_SERVICE_ENDPOINT } = config;

const isJoinable = scheduledAt => dayjs().isAfter(dayjs(scheduledAt).subtract(15, 'minutes'));

const JoinLink = ({ isJoinable, handleJoin, isStarted }) => {
  const { t } = useTranslation('global');

  const button = (
    <Button isDisabled={!isJoinable} className="flex-shrink-0 flex-auto" onClick={handleJoin}>
      {isStarted ? (
        <>
          <IconCheveronOutlineLeft size="text" />
          <span className="ml-2">{t('sessions.SessionsListModule.actions.re-join')}</span>
        </>
      ) : (
        t('sessions.SessionsListModule.actions.join')
      )}
    </Button>
  );

  if (!isJoinable) {
    return (
      <Tooltip title={t('sessions.SessionsListModule.link_activation_info')}>
        <div>{button}</div>
      </Tooltip>
    );
  }

  return button;
};

JoinLink.propTypes = {
  handleJoin: PropTypes.func.isRequired,
  isJoinable: PropTypes.bool.isRequired,
  isStarted: PropTypes.bool.isRequired,
};

const Actions = ({ id, participants, started, scheduledAt, type }) => {
  const { t } = useTranslation('global');
  const { isModerator, isOrganizer, isParticipant, participant } = useSessionRoles(participants);
  const { goTo: goToMeeting } = useSessionMeetings();
  const { open } = useModal();
  const icsDownloadRef = useRef();
  const [joinable, setJoinable] = useState(isJoinable(scheduledAt));
  const participantsWithoutOrganizer = getParticipantsWithoutOrganizer(participants);
  const getICalDownloadLink = () =>
    `${PUBLIC_INSTANT_MESSAGE_SERVICE_ENDPOINT}/sessions/${id}/${participant.participantId}/icalendar.ics?tan=${participant.tan}`;

  useEffect(() => {
    let interval;

    if (!joinable) {
      interval = setInterval(() => {
        if (isJoinable(scheduledAt)) {
          setJoinable(true);
          clearInterval(interval);
        }
      }, 60e3); // check every minute if meeting is scheduled to start in 15 minutes
    }

    return () => clearInterval(interval);
  }, [joinable, scheduledAt]);

  const handleCancel = event => {
    open({ modal: 'sessionsCancel', sessionId: id });
    trackEvent(event, { zone: 'SessionsListModule' });
  };

  const handleCopy = event => {
    open({
      modal: MODALS.sessionsCreate,
      mode: participantsWithoutOrganizer.length === 2 ? 'single' : 'group',
      sessionId: id,
      copy: true,
    });
    trackEvent(event, { zone: 'SessionsListModule' });
  };

  const handleUpdate = event => {
    open({ modal: 'sessionsDetail', sessionId: id });
    trackEvent(event, { zone: 'SessionsListModule' });
  };

  const handleResend = async event => {
    open({ modal: 'sessionsResend', sessionId: id });
    trackEvent(event, { zone: 'SessionsListModule' });
  };

  const handleIcsDownload = () => icsDownloadRef.current.click();

  return (
    isParticipant && (
      <>
        {!isOrganizer && (
          <JoinLink
            handleJoin={() => goToMeeting({ id })}
            isJoinable={joinable}
            isStarted={started}
          />
        )}
        {!isPermanentSession({ scheduledAt, type }) && (
          <PopupMenu>
            <PopupMenu.Menu>
              {(isModerator || isOrganizer) && (
                <PopupMenu.Group>
                  <PopupMenu.Item onClick={handleUpdate}>
                    <IconEditPencil size="5" className="mr-2" />
                    {t('sessions.SessionsListModule.actions.update')}
                  </PopupMenu.Item>
                </PopupMenu.Group>
              )}
              {(isModerator || isOrganizer) && (
                <PopupMenu.Group>
                  <PopupMenu.Item onClick={handleCancel}>
                    <IconCloseOutline size="5" className="mr-2" />
                    {t('sessions.SessionsListModule.actions.cancel')}
                  </PopupMenu.Item>
                </PopupMenu.Group>
              )}
              {(isModerator || participant) && (
                <PopupMenu.Group>
                  <PopupMenu.Item onClick={handleCopy}>
                    <IconDateAdd size="6" className="mr-2" />
                    {t('sessions.SessionsListModule.actions.copy')}
                  </PopupMenu.Item>
                </PopupMenu.Group>
              )}
              {(isModerator || isOrganizer) && (
                <PopupMenu.Group>
                  <PopupMenu.Item onClick={handleResend}>
                    <IconEnvelope size="5" className="mr-2" />
                    {t('sessions.SessionsListModule.actions.resend')}
                  </PopupMenu.Item>
                </PopupMenu.Group>
              )}
              {(isModerator || participant) && (
                <PopupMenu.Group>
                  <PopupMenu.Item onClick={handleIcsDownload}>
                    <IconCalendar size="5" className="mr-2" />
                    <a href={getICalDownloadLink()} ref={icsDownloadRef} download>
                      {t('sessions.SessionsListModule.actions.download')}
                    </a>
                  </PopupMenu.Item>
                </PopupMenu.Group>
              )}
            </PopupMenu.Menu>
            <PopupMenu.Trigger>
              {({ triggerRef }) => (
                <div
                  ref={triggerRef}
                  className="mx-2 hover:bg-slate-300 p-1 rounded-lg text-slate-600 flex self-center flex-shrink-0 cursor-pointer"
                >
                  <IconNavigationMore />
                </div>
              )}
            </PopupMenu.Trigger>
          </PopupMenu>
        )}
      </>
    )
  );
};

Actions.propTypes = {
  id: PropTypes.string,
  provider: PropTypes.oneOf(Object.values(SessionProviders)),
  started: PropTypes.bool,
  scheduledAt: PropTypes.string,
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.string,
    }),
  ),
  type: PropTypes.oneOf(Object.values(SESSION_TYPES)),
};

export default Actions;
